import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import BaseModalComponent from './BaseModal.component';
import {
  EventTriggerPayloadField,
  EventTriggerPayloadFieldPropsHookTypes,
  EventTriggerPayloadFieldTypes,
  EventTriggerPayloadFooterBlock,
  EventTriggerPayloadFooterBlockType,
  EventTriggerPayloadRequestMethod,
  EventTriggerType,
} from '../../redux/Event/Trigger/EventTrigger.type';
import InputComponent from '../Input/Input.component';
import { useTranslation } from 'react-i18next';
import SelectComponent from '../Input/Select.component';
import { uniqueId } from 'lodash';
import useEmployeeHook from '../../hooks/useEmployee.hook';
import UserBadgeComponent from '../UserBadge.component';
import styled from 'styled-components';
import TextareaComponent from '../Input/Textarea.component';
import ButtonComponent from '../Button.component';
import { useModalManager } from '../../hooks/ModalManager.hook';
import eventTriggerService from '../../redux/Event/Trigger/EventTrigger.service';
import ApiWrapper from '../../hooks/useApi.hook';
import ErrorAlertComponent from '../Alert/ErrorAlert.component';

export interface ModalBuilderTypes extends EventTriggerType {}

const ModalBuilderFooterBlock = styled.div`
  display: flex;
  gap: 10px;
`;
const ModalBuilderFooterLabel = styled.span`
  color: ${({ theme }) => theme.colors.LABEL};
`;
const ModalBuilderFooterLeft = styled(ModalBuilderFooterBlock)``;
const ModalBuilderFooterRight = styled(ModalBuilderFooterBlock)``;
const ModalBuilderFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0 0 0;
`;
const ModalBuilderBodyStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;
const StyledSelectOption = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ModalBuilderComponent = ({ template, payload }: ModalBuilderTypes) => {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModalManager();
  const [valueFields, setValueFields] = useState({});
  const { getSelectList: getSelectListEmployees } = useEmployeeHook();
  const [forceUpdateKey, forceUpdate] = useReducer(x => x + 1, 0);

  const getHook = (hook: string) => {
    switch (hook) {
      case EventTriggerPayloadFieldPropsHookTypes.EMPLOYEES:
        return getSelectListEmployees;
      default:
        return undefined;
    }
  };

  const getHookValue = (hook: string, value: any) => {
    switch (hook) {
      case EventTriggerPayloadFieldPropsHookTypes.EMPLOYEES:
        return {
          value: value.id,
          label: (
            <StyledSelectOption>
              <UserBadgeComponent info={value} />
            </StyledSelectOption>
          ),
        };
      default:
        return undefined;
    }
  };

  useEffect(() => {
    if (!payload?.fields) return;

    for (const field of Object.values(payload.fields)) {
      if (!field.props || !field.props.value) return;
      setValueFields(_fields => {
        const fields = _fields;
        // @ts-ignore
        if (fields[field.name]) {
          // @ts-ignore
          fields[field.name] = [
            // @ts-ignore
            ...fields[field.name!],
            field.props!.hook
              ? getHookValue(field.props!.hook!, field.props!.value!)
              : field.props!.value,
          ];
        } else {
          // @ts-ignore
          fields[field.name] = field.props!.hook
            ? getHookValue(field.props!.hook!, field.props!.value!)
            : field.props!.value;
        }

        forceUpdate();
        return fields;
      });
    }
  }, []);

  const handleSelectDynamicValue = useCallback(
    (name: string) => (value: any) => {
      setValueFields(_fields => {
        const fields = _fields;
        // @ts-ignore
        fields[name] = value;

        forceUpdate();

        return fields;
      });
    },
    [valueFields],
  );

  const handleInputDynamicValue = useCallback(
    (name: string) => (value: any) => {
      setValueFields(_fields => {
        const fields = _fields;
        // @ts-ignore
        fields[name] = value;

        return fields;
      });
    },
    [valueFields],
  );

  const renderField = useCallback(
    (field: EventTriggerPayloadField) => {
      if (field.type === EventTriggerPayloadFieldTypes.SELECT) {
        return (
          <SelectComponent
            key={uniqueId(`builder-field-${field.name}-`)}
            label={`${t(field.label)}:`}
            loadOptions={
              field.props && field.props.hook
                ? getHook(field.props.hook)
                : undefined
            }
            onChangeOption={handleSelectDynamicValue(field.name)}
            // @ts-ignore
            value={valueFields[field.name]}
            additional={field.props ? field.props.additional : false}
            isSearchable={field.props ? !!field.props.isSearchable : false}
            isMulti={field.props ? !!field.props.isMulti : false}
            useMenuPortalTarget
          />
        );
      }

      if (field.type === EventTriggerPayloadFieldTypes.TEXTAREA) {
        return (
          <TextareaComponent
            key={uniqueId(`builder-field-${field.name}-`)}
            label={`${t(field.label)}:`}
            onChange={handleInputDynamicValue(field.name)}
            // @ts-ignore
            value={valueFields[field.name]}
            readonly={field.props ? !!field.props.isReadonly : false}
            disabled={field.props ? !!field.props.isDisabled : false}
          />
        );
      }

      if (field.type === EventTriggerPayloadFieldTypes.INPUT) {
        return (
          <InputComponent
            key={uniqueId(`builder-field-${field.name}-`)}
            onChange={handleInputDynamicValue(field.name)}
            label={`${t(field.label)}:`}
            // @ts-ignore
            value={valueFields[field.name]}
            readonly={field.props ? !!field.props.isReadonly : false}
            disabled={field.props ? !!field.props.isDisabled : false}
          />
        );
      }

      return undefined;
    },
    [valueFields],
  );

  const handleSubmit = useCallback(() => {
    if (!payload || !payload.request) return;

    let apiReq = null;
    let tabLoc = null;

    switch (payload.request.method) {
      case EventTriggerPayloadRequestMethod.POST:
        apiReq = eventTriggerService.sendPost;
        break;
      case EventTriggerPayloadRequestMethod.TAB:
        tabLoc = window.location;
        break;
      default:
        return;
    }

    const data = {};

    Object.keys(valueFields).map((key: string) => {
      // @ts-ignore
      const value = valueFields[key];

      if (Array.isArray(value)) {
        // @ts-ignore
        data[key] = value.map((item: any) =>
          // @ts-ignore
          typeof item === 'object' ? item.value : item,
        );
      } else {
        // @ts-ignore
        data[key] = typeof value === 'object' ? value.value : value;
      }
    });

    if (tabLoc) window.location.href = payload.request.url;
    if (!apiReq) return;

    ApiWrapper(
      apiReq(payload.request.url, {
        template_id: template.id,
        fields: data,
      }),
      true,
    ).catch((err: any) => {
      openModal(ErrorAlertComponent, {
        header: t('Error'),
        children: JSON.stringify(err),
        onClose: () => {},
      });

      throw err;
    });
  }, [template, payload, valueFields]);

  const body = useMemo(() => {
    if (!payload || !payload.fields) return undefined;

    return (
      <ModalBuilderBodyStack>
        {Object.values(payload.fields).map(field => renderField(field))}
      </ModalBuilderBodyStack>
    );
  }, [payload, forceUpdateKey, valueFields]);

  const renderFooterBlock = (block: EventTriggerPayloadFooterBlock) => {
    switch (block.type) {
      case EventTriggerPayloadFooterBlockType.LABEL:
        return (
          <ModalBuilderFooterLabel
            key={uniqueId(`footer-block-${block.type}-`)}
          >
            {t(block.label)}
          </ModalBuilderFooterLabel>
        );
      case EventTriggerPayloadFooterBlockType.SUBMIT:
        return (
          <ButtonComponent
            appearance="primary"
            key={uniqueId(`footer-block-${block.type}-`)}
            handleClick={() => {
              handleSubmit();
              block.after_close && closeModal();
            }}
          >
            {t(block.label)}
          </ButtonComponent>
        );
      case EventTriggerPayloadFooterBlockType.CANCEL:
        return (
          <ButtonComponent
            key={uniqueId(`footer-block-${block.type}-`)}
            handleClick={() => closeModal()}
          >
            {t(block.label)}
          </ButtonComponent>
        );
      default:
        return undefined;
    }
  };

  const footer = useMemo(() => {
    if (!payload || !payload.footer) return undefined;

    const leftFooter = payload.footer.left ? (
      <ModalBuilderFooterLeft>
        {payload.footer.left.map(renderFooterBlock)}
      </ModalBuilderFooterLeft>
    ) : (
      <div />
    );
    const rightFooter = payload.footer.right ? (
      <ModalBuilderFooterRight>
        {payload.footer.right.map(renderFooterBlock)}
      </ModalBuilderFooterRight>
    ) : (
      <div />
    );

    return (
      <ModalBuilderFooter>
        {leftFooter}
        {rightFooter}
      </ModalBuilderFooter>
    );
  }, [payload]);

  return (
    <BaseModalComponent
      header={template.name}
      footer={footer}
      style={payload ? payload.modalStyle : undefined}
    >
      {body}
    </BaseModalComponent>
  );
};

export default ModalBuilderComponent;
